import { useCallback, useMemo } from 'react';
import { pickBy } from 'lodash';
import { bianka, BIANKA_ACTIVITY_TYPES, logger } from '@fiverr-private/obs';
import { ClickType, ElementType } from '@fiverr-private/bianka_js';
import type { Click } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/click';
import {
    NavigationBar,
    type NavigationBar_Node,
} from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/navigation_bar';
import { useHeaderContext } from '../../modern/context/HeaderContext';
import type { ReportData } from '../../modern/components/CategoriesNavigation/transformer/types';

const FEATURE = 'navigation_bar';

const EVENTS = {
    OPEN: { type: 'click_on_open_nav_bar_categories', elementName: 'categories_button' },
    OPTION: { type: 'click_on_nav_bar_option', elementName: 'nav_bar_option' },
    EXPLORE: { type: 'click_on_nav_bar_explore_category', elementName: 'explore_category' },
} as const;

type NavigationBarEvent = (typeof EVENTS)[keyof typeof EVENTS];

interface Item {
    reportData: ReportData;
}

export interface NavigationBarPath {
    section: Item;
    category?: Item;
    subCategory?: Item;
    nestedSubCategory?: Item;
}

type Node = NavigationBar_Node | undefined;

const nameOnly = (item?: Item): Node => item && { name: item.reportData.alias };

const ancestorNode = (item: Item | undefined, key: keyof ReportData, id?: string): Node => {
    if (!id) {
        return undefined;
    }

    const ownId = item?.reportData[key];
    const name = !ownId || ownId === id ? item?.reportData.alias : undefined;

    return { ...(name && { name }), id };
};

const resolveMessage = ({ section, category, subCategory, nestedSubCategory }: NavigationBarPath) => {
    const viewOption = section.reportData.alias;
    const clicked = nestedSubCategory ?? subCategory ?? category;

    if (!clicked) {
        return { viewOption };
    }

    const { alias, categoryId, subCategoryId, nestedSubCategoryId } = clicked.reportData;
    const heading = nestedSubCategory && subCategory;

    if (nestedSubCategoryId) {
        return {
            viewOption,
            category: ancestorNode(category, 'categoryId', categoryId),
            subCategory: ancestorNode(heading, 'subCategoryId', subCategoryId),
            leaf: { name: alias, id: nestedSubCategoryId },
        };
    }

    if (subCategoryId) {
        return {
            viewOption,
            category: ancestorNode(category, 'categoryId', categoryId),
            subCategory: { name: alias, id: subCategoryId },
        };
    }

    if (categoryId) {
        return { viewOption, category: { name: alias, id: categoryId } };
    }

    return {
        viewOption,
        category: nameOnly(category),
        subCategory: nameOnly(subCategory),
        leaf: nameOnly(nestedSubCategory),
    };
};

const buildMessage = (path: NavigationBarPath): Partial<NavigationBar> =>
    pickBy(resolveMessage(path), (value) => value !== undefined);

export const useNavigationBarReporter = () => {
    const { pageCtxId, appName = '', user } = useHeaderContext();
    const userId = user?.id;

    const reportClickActivity = useCallback(
        (event: NavigationBarEvent, elementType: ElementType, message: Partial<NavigationBar> = {}) => {
            try {
                bianka.reportActivity<Click>({
                    type: BIANKA_ACTIVITY_TYPES.CLICK,
                    event: {
                        type: event.type,
                        clickType: ClickType.UNSPECIFIED,
                        element: { name: event.elementName, type: elementType },
                        ...(userId && { user: { id: userId } }),
                    },
                    ...(pageCtxId && { page: { name: appName, ctxId: pageCtxId } }),
                    additionalData: {
                        type: NavigationBar,
                        message: { feature: FEATURE, pageName: appName, ...message },
                    },
                });
            } catch (error) {
                logger.error(error as Error, { description: `Failed to report ${event.type} to Bianka` });
            }
        },
        [pageCtxId, appName, userId]
    );

    return useMemo(
        () => ({
            reportOpen: () => reportClickActivity(EVENTS.OPEN, ElementType.BUTTON),
            reportOption: (path: NavigationBarPath) =>
                reportClickActivity(
                    EVENTS.OPTION,
                    path.subCategory ? ElementType.LINK : ElementType.BUTTON,
                    buildMessage(path)
                ),
            reportExplore: (path: Required<Pick<NavigationBarPath, 'section' | 'category'>>) =>
                reportClickActivity(EVENTS.EXPLORE, ElementType.LINK, buildMessage(path)),
        }),
        [reportClickActivity]
    );
};
